import { useState, useCallback, useEffect } from "react";
import EmailTemplate from "../components/EmailTemplate";
import PortalPopup from "../components/PortalPopup";
import CTAs from "../components/CTAs";
import { Judge } from "iconsax-reactjs";

const LawFirm = ({ onClose, debtorId, formType, debtorAddress }) => {
  const [isEmailTemplateOpen, setEmailTemplateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [lawFirms, setLawFirms] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  // Function to geocode address to coordinates
  const geocodeAddress = (address) => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps?.Geocoder) {
        console.error("Google Geocoding API not available.");
        reject(new Error("Geocoding API not available"));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      const fullAddress = `${address.house}, ${address.street}, ${address.city}, ${address.country}, ${address.postalCode}`;

      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          console.warn("Geocoding failed:", status);
          // Fallback to a default location (London, UK)
          resolve({ lat: 51.5074, lng: -0.1278 });
        }
      });
    });
  };

  const fetchLawFirms = async (location) => {
    const { lat, lng } = location;
    const map = new window.google.maps.Map(document.createElement("div"));
    const service = new window.google.maps.places.PlacesService(map);

    const request = {
      location: { lat, lng },
      radius: 15000, // Increased radius to 15km for better results
      query: "law firm",
    };

    return new Promise((resolve) => {
      service.textSearch(request, (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results.length > 0
        ) {
          const dynamicFirms = results.slice(0, 10).map((place) => ({
            name: place.name,
            email: `contact@${place.name
              .replace(/\s+/g, "")
              .toLowerCase()}.com`,
            address: place.formatted_address,
            rating: place.rating || "N/A",
            isRecommended: place.rating && place.rating > 4.0,
          }));
          resolve(dynamicFirms);
        } else {
          console.warn("No law firms found or request failed", status);
          resolve([]);
        }
      });
    });
  };

  useEffect(() => {
    const initializeLawFirms = async () => {
      if (!window.google?.maps?.places) {
        console.error("Google Places API not available.");
        return;
      }

      try {
        let location;

        // If debtorAddress is provided, use it for geocoding
        if (debtorAddress && debtorAddress.city && debtorAddress.country) {
          console.log("Using debtor address:", debtorAddress);
          location = await geocodeAddress(debtorAddress);
        } else {
          // Fallback to user's current location
          console.log("Using user's current location as fallback");
          location = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                });
              },
              () => {
                // Fallback to London, UK if geolocation fails
                resolve({ lat: 51.5074, lng: -0.1278 });
              },
            );
          });
        }

        console.log("Searching for law firms near:", location);
        const firms = await fetchLawFirms(location);
        setLawFirms(firms);
      } catch (error) {
        console.error("Error initializing law firms:", error);
        // Set empty array as fallback
        setLawFirms([]);
      }
    };

    initializeLawFirms();
  }, [debtorAddress]);

  const saveFirmToDatabase = async () => {
    if (!selectedFirm) {
      alert("Please select a law firm before proceeding");
      return false;
    }

    try {
      setIsLoading(true);
      const dataToStore = {
        firmName: selectedFirm.name,
        firmEmail: selectedFirm.email,
        firmAddress: selectedFirm.address,
        firmRating: selectedFirm.rating,
        formType: formType,
        formModelName: formType === "lba" ? "lbaFormModel" : "lpdFormModel",
        debtorId: debtorId,
        searchLocation: debtorAddress, // Store the location used for search
      };

      const response = await fetch(`${API_URL}/api/law_firm/store-law-firm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToStore),
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result?.details || result?.error || "Failed to store data");
        setIsLoading(false);
        return false;
      } else {
        alert(
          `Email sent successfully and lawyer will be in touch with you soon !!`,
        );
        onClose();
      }

      return true;
    } catch (error) {
      console.error("Error storing data:", error);
      alert(error.message || "Something went wrong. Please try again.");
      setIsLoading(false);
      return false;
    }
  };

  const openEmailTemplate = useCallback(async () => {
    const saveSuccess = await saveFirmToDatabase();
    if (saveSuccess) {
      setEmailTemplateOpen(false);
    }
  }, [selectedFirm]);

  const closeEmailTemplate = useCallback(() => {
    setEmailTemplateOpen(false);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  const selectFirm = useCallback((firm) => {
    setSelectedFirm(firm);
    setIsDropdownOpen(false);
  }, []);

  return (
    <>
      <div className="relative shadow-[0px_10px_60px_rgba(0,_0,_0,_0.1)] rounded-3xs bg-neutrals-neutral-50 box-border w-full flex flex-col items-start justify-start gap-[24px] text-left text-sm text-neutrals-neutral-900 font-open-sans-ff-meta-strong border-[1px] border-solid border-neutrals-neutral-100">
        <div className="self-stretch flex flex-col items-start justify-start pt-6 px-6 pb-0 gap-[24px] text-lg">
          <div className="w-full flex flex-row items-start justify-between">
            <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-row items-start justify-start p-2.5 border-[1px] border-solid border-neutrals-neutral-100">
              <Judge size={20} />
            </div>
            <img
              className="w-5 h-5 cursor-pointer"
              alt="Close"
              src="assets/cancel.svg"
              onClick={onClose}
            />
          </div>
          <div className="flex flex-col items-start gap-[6px]">
            <div className="font-semibold">Select your preferred law firm</div>
            <div className="text-sm text-design-light-mode-neutral-neutral-300">
              Send mail directly to your law firm to investigate further.
              {debtorAddress && (
                <div className="mt-1 text-xs">
                  Showing firms near: {debtorAddress.city},{" "}
                  {debtorAddress.country}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="self-stretch flex flex-col items-start justify-start py-0 px-6">
          <div className="w-full flex flex-col items-start gap-2.5">
            <div className="font-semibold">
              {debtorAddress
                ? "Law firms near debtor location"
                : "Nearby law firms"}
            </div>
            <div className="relative w-full">
              <div
                className="rounded-3xs bg-neutrals-neutral-50 flex justify-between border-solid items-center py-3.5 px-2.5 text-base text-neutrals-neutral-300 border border-neutrals-neutral-100 cursor-pointer"
                onClick={toggleDropdown}
              >
                <div className="truncate">
                  {selectedFirm ? selectedFirm.name : "Select"}
                </div>
                <img
                  className="w-5 h-5 object-contain"
                  alt=""
                  src="assets/arrow-down.svg"
                />
              </div>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 z-10 w-full mt-1 bg-neutrals-neutral-50 border border-neutrals-neutral-100 rounded-3xs shadow-lg max-h-64 overflow-y-auto">
                  {lawFirms.length > 0 ? (
                    lawFirms.map((firm, index) => (
                      <div
                        key={index}
                        className="flex flex-col py-3 px-4 border-b border-neutrals-neutral-100 last:border-b-0 hover:bg-neutrals-neutral-100 cursor-pointer"
                        onClick={() => selectFirm(firm)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex flex-col">
                            <span className="font-medium">{firm.name}</span>
                            {firm.address && (
                              <span className="text-xs text-design-light-mode-neutral-neutral-300 mt-1">
                                {firm.address}
                              </span>
                            )}
                          </div>
                          {firm.isRecommended && (
                            <span className="text-xs  text-white px-2 py-1 rounded">
                              <img
                                class="w-5 h-5"
                                alt=""
                                src="assets/judge.svg"
                              />
                            </span>
                          )}
                        </div>
                        {firm.rating !== "N/A" && (
                          <div className="flex items-center mt-1">
                            <span className="text-xs text-design-light-mode-neutral-neutral-300">
                              Rating: {firm.rating}/5
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="py-3 px-4 text-sm text-design-light-mode-neutral-neutral-300">
                      {debtorAddress
                        ? `No law firms found near ${debtorAddress.city}, ${debtorAddress.country}`
                        : "No law firms found"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="self-stretch flex justify-center gap-3 pb-6 px-6">
          <CTAs
            type="Secondarybtn"
            text="Cancel"
            textColor="text-neutrals-neutral-900"
            bgColor="bg-neutrals-neutral-50"
            borderColor="border-neutrals-neutral-100"
            width="100%"
            onClick={onClose}
          />
          <CTAs
            type="Primarybtn"
            text={isLoading ? "Processing..." : "Save"}
            textColor="text-neutrals-neutral-50"
            bgColor="bg-primary-primary-500"
            borderColor="border-primary-primary-500"
            width="100%"
            onClick={openEmailTemplate}
            disabled={isLoading}
          />
        </div>
      </div>

      {isEmailTemplateOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeEmailTemplate}
        >
          <EmailTemplate onClose={closeEmailTemplate} />
        </PortalPopup>
      )}
    </>
  );
};

export default LawFirm;
